import { useQuery } from '@tanstack/react-query';

import { DetailSheet } from '@/components/composed/DetailSheet';
import { DateCell } from '@/components/composed/DateCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { clientDetailQuery } from '../api/client-queries';
import type { ClientId } from '@/lib/api/ids';

export function ClientDetailSheet({
  clientId,
  open,
  onOpenChange,
}: {
  clientId: ClientId | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <DetailSheet open={open} onOpenChange={onOpenChange} title="Client detail">
      {clientId ? <Body clientId={clientId} /> : null}
    </DetailSheet>
  );
}

function Body({ clientId }: { clientId: ClientId }) {
  const query = useQuery(clientDetailQuery(clientId));
  if (query.isLoading) return <Skeleton className="h-24 w-full" />;
  if (query.isError || !query.data)
    return <p className="text-sm text-destructive">Failed to load client.</p>;

  const client = query.data;
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <dt className="text-xs text-muted-foreground">Name</dt>
        <dd className="font-medium">{client.name}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-xs text-muted-foreground">Status</dt>
        <dd className="font-medium">
          <StatusBadge
            label={client.status === 'active' ? 'Active' : 'Inactive'}
            tone={client.status === 'active' ? 'success' : 'neutral'}
          />
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-xs text-muted-foreground">Created</dt>
        <dd className="font-medium">
          <DateCell value={client.createdAt} />
        </dd>
      </div>
    </dl>
  );
}
